import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDownloadAlbumById } from '@bpm-web-app/swr-hooks';
import {
    downloadAlbumWithMediaToQueueItem,
    showToast,
    HLSPlayer,
    isServerSide,
    parseSeconds,
    PlayerState,
    PLAYER_PREVIEW_SECONDS,
    State as PlayerStateEnum,
    useHubSwitch,
} from '@bpm-web-app/utils';
import { User } from '@bpm-web-app/supreme-api-sdk';
import styles from './player.module.css';
import { usePlayer, usePlayerDuration } from './player.context';
import { NavContext } from '../nav/nav.context';
import { MiniPlayer } from '../mini-player/mini-player';
import { MaxiPlayer } from '../maxi-player/maxi-player';
import { MobileDrawer } from '../mobile-drawer/mobile-drawer';
import { FiltersContext } from '../filters/filters.context';
import { useCrates } from '../shared/three-dots-sheet/useCrates';
import AddToCrate from '../../assets/icons/add-to-crate.svg';
import AddToQueue from '../../assets/icons/add-to-queue.svg';
import FindSimilar from '../../assets/icons/find-similar.svg';
import { useSession } from '../shared/session-provider/session-provider';
import { ArtistMiniPlayer } from '../mini-player/artist-mini-player';

export function Player({ isArtistPortal = false }: { isArtistPortal?: boolean }) {
    const {
        isOpen,
        isShuffling,
        isRepeating,
        volume,
        hasNextTrack,
        onTrackEnd,
        setElapsed,
        setStartOffset,
        isMaxiPlayer,
        currentTrack,
        replaceCurrentPlayingTrack,
        useSdk,
        addToQueue,
        setShowSimilarTracks,
        nextTrack,
        isPreviewTrack,
        currentDuration,
        setIsMaxiPlayer
    } = usePlayer();

    const { elapsed } = usePlayerDuration();

    const { isNavOpen } = useContext(NavContext);
    const { isFiltersOpen } = useContext(FiltersContext);
    const { isDownload } = useHubSwitch();
    const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false);
    const { data: downloadAlbumData } = useDownloadAlbumById(currentTrack?.album_id, isDownload);
    const [playerState, setPlayerState] = useState<PlayerStateEnum>(PlayerState.STATE);
    const { addMediaToOnlineCrate, addMediaToOfflineCrate } = useCrates();
    const { userMemberships, platformUser } = useSession();

    const addVersionToCrate = useCallback(
        (versionId) => {
            if (isDownload) {
                addMediaToOnlineCrate(versionId);
            } else {
                addMediaToOfflineCrate(versionId);
            }
        },
        [addMediaToOfflineCrate, addMediaToOnlineCrate, isDownload]
    );

    const addVersionToQueue = useCallback(
        (queueItem) => {
            addToQueue([queueItem]);
            showToast({ type: 'success', message: 'Added to queue.', buttonText: 'Open Queue', onButtonClick: () => setIsMaxiPlayer(true) });
        },
        [addToQueue, setIsMaxiPlayer]
    );

    useEffect(() => {
        PlayerState.onPlayerStateChange(setPlayerState);
        PlayerState.onProgress(setElapsed);
        PlayerState.onStartOffset(setStartOffset);
        return () => {
            PlayerState.offPlayerStateChange(setPlayerState);
            PlayerState.offProgress(setElapsed);
            PlayerState.offStartOffset(setStartOffset);
        };
    }, [setElapsed, setStartOffset]);

    useEffect(() => {
        if (playerState === PlayerStateEnum.Ended && hasNextTrack) onTrackEnd();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerState]);

    const streamUrl = useMemo<string>(() => {
        const type = currentTrack?.stream_info?.audioInfo?.type;
        const isHQ = true;
        const quality = isHQ ? 'hq' : 'lq';
        return currentTrack?.stream_info?.audioInfo?.[quality][type];
    }, [currentTrack]);

    useEffect(() => {
        if (!currentTrack) return;
        if (playerState === PlayerStateEnum.Playing) {
            HLSPlayer.stop();
        }

        HLSPlayer.load(streamUrl);
        HLSPlayer.play();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTrack, useSdk]);

    useEffect(() => {
        if (!currentTrack) return;
        HLSPlayer.setVolume(volume / 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volume]);

    useEffect(() => {
        if (!isPreviewTrack) return;

        if (elapsed >= (currentDuration || PLAYER_PREVIEW_SECONDS)) {
            const freeListenerMessage = 'Keep listening. Subscribe now.';
            const activeSubscriberMessage = 'Preview limit reached. Download for full version.';
            const submitMembershipStatusMessage = 'Get full access. Request a membership now.';

            const hasSubscription = isDownload ? userMemberships.download?.has_membership : userMemberships.stream?.has_membership;

            let message = hasSubscription ? activeSubscriberMessage : freeListenerMessage;

            if (isDownload && platformUser.download?.verification_status !== User.VerificationStatusEnum.Verified) {
                message = submitMembershipStatusMessage;
            }
            showToast({ type: 'error', title: message, buttonText: hasSubscription ? undefined : 'View Plans', replaceRouteOnButtonClick: hasSubscription ? undefined : '/account/plan' });

            nextTrack();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elapsed, currentDuration]);

    if (isServerSide()) return null;

    return (
        <>
            {/* eslint-disable-next-line @typescript-eslint/dot-notation */}
            <div className={classNames(styles['player'], {
                [styles['player--open']]: isOpen,
                [styles['player--playing']]: playerState === PlayerStateEnum.Playing,
                [styles['player--shuffling']]: isShuffling,
                [styles['player--repeat']]: isRepeating,
                [styles['player--muted']]: !volume,
                [styles['player--nav-open']]: isNavOpen,
                [styles['player--filters-open']]: isFiltersOpen,
                [styles['player--maxi-player']]: isMaxiPlayer,
                [styles['player--artist']]: isArtistPortal,
            })}
            >
                {!!currentTrack && isMaxiPlayer && <MaxiPlayer playerState={playerState} setIsVersionMenuOpen={setIsVersionMenuOpen} />}
                {!!currentTrack && !isMaxiPlayer && !isArtistPortal && <MiniPlayer playerState={playerState} />}
                {!!currentTrack && !isMaxiPlayer && isArtistPortal && <ArtistMiniPlayer playerState={playerState} />}
            </div>
            <MobileDrawer isOpen={isVersionMenuOpen} onClose={() => setIsVersionMenuOpen(false)}>
                <div className={styles['player__versions-menu']}>
                    {isDownload &&
                        downloadAlbumData?.data.media?.map((version, index) => (
                            <div key={version.id} className={styles['player__versions-menu-item']}>
                                <button
                                    type="button"
                                    // This should replace current playing track;
                                    onClick={() => {
                                        replaceCurrentPlayingTrack(downloadAlbumWithMediaToQueueItem(downloadAlbumData.data, index));
                                    }}
                                    aria-label={`Play ${version.name}`}
                                    className={styles['player__versions-menu-item-play']}
                                >
                                    <i className="icon icon--play" />
                                </button>
                                <div className={styles['player__versions-menu-item-title']}>{version.name}</div>
                                <div className={styles['player__versions-menu-item-duration']}>{parseSeconds(version.estimated_duration)}</div>
                                <button
                                    type="button"
                                    className={styles['player__versions-menu-item-button']}
                                    onClick={() => addVersionToQueue(downloadAlbumWithMediaToQueueItem(downloadAlbumData.data, index))}
                                >
                                    <AddToQueue />
                                </button>
                                <button type="button" className={styles['player__versions-menu-item-button']} onClick={() => addVersionToCrate(version.id)}>
                                    <AddToCrate />
                                </button>
                                <button type="button" className={styles['player__versions-menu-item-button']} onClick={() => setShowSimilarTracks(true)}>
                                    <FindSimilar />
                                </button>
                            </div>
                        ))}
                </div>
            </MobileDrawer>
        </>
    );
}
